import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { TeamCreateSchema, updateTeam } from "@/queries/team";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { TeamForm, TeamFormValues, teamSchema } from "./TeamForm";

interface UpdateTeamDialogProps {
  team: {
    id: number;
    name: string;
    description: string;
    leader?: {
      id: number;
      email: string;
      name: string;
    } | null;
  };
}

export function UpdateTeamDialog({ team }: UpdateTeamDialogProps) {
  const { mutate: updateTeamMutation, isPending: isUpdatingTeam } =
    updateTeam();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);

  const form = useForm<TeamFormValues>({
    resolver: zodResolver(teamSchema),
    defaultValues: {
      name: "",
      description: "",
      leader_id: undefined,
    },
  });

  // Pre-fill form when the dialog opens or the team data changes
  useEffect(() => {
    if (isUpdateModalOpen && team) {
      form.reset({
        name: team.name,
        description: team.description,
        leader_id: team.leader?.id,
      });
    } else if (!isUpdateModalOpen) {
      form.reset({}); // Clear form when closing
    }
  }, [isUpdateModalOpen, team, form.reset]);

  // Submit Handler
  const onTeamSubmit = (values: TeamFormValues) => {
    const teamData: TeamCreateSchema = {
      name: values.name,
      description: values.description,
      leader_id: values.leader_id,
    };

    updateTeamMutation(
      { teamId: team.id, teamData },
      {
        onSuccess: () => {
          toast({
            title: "Success",
            description: "Team updated successfully.",
          });
          queryClient.invalidateQueries({ queryKey: ["teams"] });
          setIsUpdateModalOpen(false);
        },
        onError: (err) => {
          toast({
            variant: "destructive",
            title: "Error updating team",
            description: err.message,
          });
        },
      }
    );
  };

  return (
    <Dialog open={isUpdateModalOpen} onOpenChange={setIsUpdateModalOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon">
          <Pencil className="h-4 w-4" />
          <span className="sr-only">Edit Team</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Team</DialogTitle>
          <DialogDescription>
            Modify the details for this team.
          </DialogDescription>
        </DialogHeader>

        {/* Use the shared form component */}
        <div className="py-4">
          <TeamForm
            form={form}
            onSubmit={onTeamSubmit}
            isLoading={isUpdatingTeam}
            submitButtonText="Save Changes"
            onClose={() => setIsUpdateModalOpen(false)}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
